import React, { useState, useEffect, useCallback } from 'react';
import { statsApi } from '../../services/api';
import './UserEngagement.css';

/**
 * Issue #291: User Engagement KPI Dashboard
 * Displays MAU, DAU, and DAU/MAU ratio with graphs
 */
function UserEngagement() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ mau: [], dau: [], dau_mau_ratio: [] });

  // Date range filter - Issue #322: Default to 17.03.2026
  const MIN_DATE = '2026-03-17';
  const [startDate, setStartDate] = useState(MIN_DATE);
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  // Graph type toggle (line/bar)
  const [mauGraphType, setMauGraphType] = useState('line');
  const [dauGraphType, setDauGraphType] = useState('line');
  const [ratioGraphType, setRatioGraphType] = useState('line');

  // Export selection
  const [exportKpis, setExportKpis] = useState({
    mau: true,
    dau: true,
    dau_mau_ratio: true
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await statsApi.getUserEngagement(startDate, endDate);
      setData(response.data);
    } catch (err) {
      console.error('Failed to fetch user engagement data:', err);
      setError('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = async () => {
    const selectedKpis = Object.entries(exportKpis)
      .filter(([_, selected]) => selected)
      .map(([kpi]) => kpi);

    if (selectedKpis.length === 0) {
      alert('Bitte wählen Sie mindestens einen KPI zum Exportieren aus.');
      return;
    }

    try {
      const response = await statsApi.exportUserEngagement(selectedKpis, startDate, endDate);
      // Create download link
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `user-engagement-${startDate}-${endDate}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Export failed:', err);
      alert('Export fehlgeschlagen');
    }
  };

  // Calculate max values for scaling graphs
  const maxMau = Math.max(...data.mau.map(d => d.count), 1);
  const maxDau = Math.max(...data.dau.map(d => d.count), 1);
  const maxRatio = Math.max(...data.dau_mau_ratio.map(d => d.ratio), 1);

  if (loading) {
    return (
      <div className="user-engagement-container">
        <h1>User Engagement</h1>
        <div className="loading">Lade Daten...</div>
      </div>
    );
  }

  return (
    <div className="user-engagement-container">
      <h1>User Engagement</h1>

      {error && <div className="error-message">{error}</div>}

      {/* Time Period Filter - Issue #322: Min date 17.03.2026 */}
      <div className="filter-section">
        <div className="date-filters">
          <label>
            Von:
            <input
              type="date"
              value={startDate}
              min={MIN_DATE}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </label>
          <label>
            Bis:
            <input
              type="date"
              value={endDate}
              min={MIN_DATE}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </label>
          <button onClick={fetchData} className="refresh-btn">
            Aktualisieren
          </button>
        </div>
      </div>

      {/* Export Section */}
      <div className="export-section">
        <h3>KPIs exportieren</h3>
        <div className="export-options">
          <label>
            <input
              type="checkbox"
              checked={exportKpis.mau}
              onChange={(e) => setExportKpis({...exportKpis, mau: e.target.checked})}
            />
            MAU
          </label>
          <label>
            <input
              type="checkbox"
              checked={exportKpis.dau}
              onChange={(e) => setExportKpis({...exportKpis, dau: e.target.checked})}
            />
            DAU
          </label>
          <label>
            <input
              type="checkbox"
              checked={exportKpis.dau_mau_ratio}
              onChange={(e) => setExportKpis({...exportKpis, dau_mau_ratio: e.target.checked})}
            />
            DAU/MAU Ratio
          </label>
          <button onClick={handleExport} className="export-btn">
            Als Excel exportieren
          </button>
        </div>
      </div>

      {/* MAU Section - Issue #322: Y-axis from 0, exclusive graph type */}
      <div className="kpi-section">
        <div className="kpi-header">
          <h2>Monthly Active Users (MAU)</h2>
          <div className="graph-toggle">
            <button
              className={mauGraphType === 'line' ? 'active' : ''}
              onClick={() => setMauGraphType('line')}
            >
              Linie
            </button>
            <button
              className={mauGraphType === 'bar' ? 'active' : ''}
              onClick={() => setMauGraphType('bar')}
            >
              Balken
            </button>
          </div>
        </div>
        <p className="kpi-description">
          Anzahl einzigartiger Nutzer mit mindestens einer Aktivität pro Monat
        </p>
        <div className="graph-container">
          {data.mau.length === 0 ? (
            <div className="no-data">Keine Daten verfügbar</div>
          ) : (
            <div className="graph-with-axis">
              <div className="y-axis">
                <span className="y-axis-label">{maxMau}</span>
                <span className="y-axis-label">{Math.round(maxMau * 0.75)}</span>
                <span className="y-axis-label">{Math.round(maxMau * 0.5)}</span>
                <span className="y-axis-label">{Math.round(maxMau * 0.25)}</span>
                <span className="y-axis-label">0</span>
              </div>
              <div className={`graph ${mauGraphType}`}>
                {mauGraphType === 'bar' && data.mau.map((point, idx) => (
                  <div key={idx} className="graph-bar-container">
                    <div
                      className="graph-bar"
                      style={{ height: `${(point.count / maxMau) * 100}%` }}
                      title={`${point.month}: ${point.count} Nutzer`}
                    >
                      <span className="bar-value">{point.count}</span>
                    </div>
                    <span className="bar-label">{point.month}</span>
                  </div>
                ))}
                {mauGraphType === 'line' && (
                  <>
                    {data.mau.map((point, idx) => (
                      <div key={idx} className="graph-bar-container line-point-container">
                        <span className="bar-label">{point.month}</span>
                      </div>
                    ))}
                    <svg className="line-overlay" viewBox={`0 0 ${data.mau.length * 60} 200`} preserveAspectRatio="none">
                      <polyline
                        fill="none"
                        stroke="#1B1B73"
                        strokeWidth="2"
                        points={data.mau.map((point, idx) =>
                          `${idx * 60 + 30},${200 - (point.count / maxMau) * 180}`
                        ).join(' ')}
                      />
                      {data.mau.map((point, idx) => (
                        <circle
                          key={idx}
                          cx={idx * 60 + 30}
                          cy={200 - (point.count / maxMau) * 180}
                          r="4"
                          fill="#1B1B73"
                        >
                          <title>{`${point.month}: ${point.count} Nutzer`}</title>
                        </circle>
                      ))}
                    </svg>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* DAU Section - Issue #322: Y-axis from 0, exclusive graph type */}
      <div className="kpi-section">
        <div className="kpi-header">
          <h2>Daily Active Users (DAU)</h2>
          <div className="graph-toggle">
            <button
              className={dauGraphType === 'line' ? 'active' : ''}
              onClick={() => setDauGraphType('line')}
            >
              Linie
            </button>
            <button
              className={dauGraphType === 'bar' ? 'active' : ''}
              onClick={() => setDauGraphType('bar')}
            >
              Balken
            </button>
          </div>
        </div>
        <p className="kpi-description">
          Anzahl einzigartiger Nutzer mit mindestens einer Aktivität pro Tag
        </p>
        <div className="graph-container">
          {data.dau.length === 0 ? (
            <div className="no-data">Keine Daten verfügbar</div>
          ) : (
            <div className="graph-with-axis">
              <div className="y-axis">
                <span className="y-axis-label">{maxDau}</span>
                <span className="y-axis-label">{Math.round(maxDau * 0.75)}</span>
                <span className="y-axis-label">{Math.round(maxDau * 0.5)}</span>
                <span className="y-axis-label">{Math.round(maxDau * 0.25)}</span>
                <span className="y-axis-label">0</span>
              </div>
              <div className={`graph ${dauGraphType}`}>
                {dauGraphType === 'bar' && data.dau.slice(-30).map((point, idx) => (
                  <div key={idx} className="graph-bar-container small">
                    <div
                      className="graph-bar"
                      style={{ height: `${(point.count / maxDau) * 100}%` }}
                      title={`${point.date}: ${point.count} Nutzer`}
                    >
                      <span className="bar-value-small">{point.count}</span>
                    </div>
                    <span className="bar-label-small">{point.date.slice(5)}</span>
                  </div>
                ))}
                {dauGraphType === 'line' && data.dau.length > 0 && (
                  <>
                    {data.dau.slice(-30).map((point, idx) => (
                      <div key={idx} className="graph-bar-container small line-point-container">
                        <span className="bar-label-small">{point.date.slice(5)}</span>
                      </div>
                    ))}
                    <svg className="line-overlay" viewBox={`0 0 ${Math.min(data.dau.length, 30) * 25} 200`} preserveAspectRatio="none">
                      <polyline
                        fill="none"
                        stroke="#F6B707"
                        strokeWidth="2"
                        points={data.dau.slice(-30).map((point, idx) =>
                          `${idx * 25 + 12},${200 - (point.count / maxDau) * 180}`
                        ).join(' ')}
                      />
                      {data.dau.slice(-30).map((point, idx) => (
                        <circle
                          key={idx}
                          cx={idx * 25 + 12}
                          cy={200 - (point.count / maxDau) * 180}
                          r="3"
                          fill="#F6B707"
                        >
                          <title>{`${point.date}: ${point.count} Nutzer`}</title>
                        </circle>
                      ))}
                    </svg>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* DAU/MAU Ratio Section - Issue #322: Y-axis from 0, exclusive graph type */}
      <div className="kpi-section">
        <div className="kpi-header">
          <h2>DAU/MAU Ratio</h2>
          <div className="graph-toggle">
            <button
              className={ratioGraphType === 'line' ? 'active' : ''}
              onClick={() => setRatioGraphType('line')}
            >
              Linie
            </button>
            <button
              className={ratioGraphType === 'bar' ? 'active' : ''}
              onClick={() => setRatioGraphType('bar')}
            >
              Balken
            </button>
          </div>
        </div>
        <p className="kpi-description">
          Verhältnis von durchschnittlichem DAU zu MAU (Stickiness-Metrik)
        </p>
        <div className="graph-container">
          {data.dau_mau_ratio.length === 0 ? (
            <div className="no-data">Keine Daten verfügbar</div>
          ) : (
            <div className="graph-with-axis">
              <div className="y-axis">
                <span className="y-axis-label">{Math.max(maxRatio, 100).toFixed(0)}%</span>
                <span className="y-axis-label">{(Math.max(maxRatio, 100) * 0.75).toFixed(0)}%</span>
                <span className="y-axis-label">{(Math.max(maxRatio, 100) * 0.5).toFixed(0)}%</span>
                <span className="y-axis-label">{(Math.max(maxRatio, 100) * 0.25).toFixed(0)}%</span>
                <span className="y-axis-label">0%</span>
              </div>
              <div className={`graph ${ratioGraphType}`}>
                {ratioGraphType === 'bar' && data.dau_mau_ratio.map((point, idx) => (
                  <div key={idx} className="graph-bar-container">
                    <div
                      className="graph-bar ratio"
                      style={{ height: `${(point.ratio / Math.max(maxRatio, 100)) * 100}%` }}
                      title={`${point.month}: ${point.ratio.toFixed(1)}% (Avg DAU: ${point.avg_dau.toFixed(0)}, MAU: ${point.mau})`}
                    >
                      <span className="bar-value">{point.ratio.toFixed(1)}%</span>
                    </div>
                    <span className="bar-label">{point.month}</span>
                  </div>
                ))}
                {ratioGraphType === 'line' && (
                  <>
                    {data.dau_mau_ratio.map((point, idx) => (
                      <div key={idx} className="graph-bar-container line-point-container">
                        <span className="bar-label">{point.month}</span>
                      </div>
                    ))}
                    <svg className="line-overlay" viewBox={`0 0 ${data.dau_mau_ratio.length * 60} 200`} preserveAspectRatio="none">
                      <polyline
                        fill="none"
                        stroke="#28a745"
                        strokeWidth="2"
                        points={data.dau_mau_ratio.map((point, idx) =>
                          `${idx * 60 + 30},${200 - (point.ratio / Math.max(maxRatio, 100)) * 180}`
                        ).join(' ')}
                      />
                      {data.dau_mau_ratio.map((point, idx) => (
                        <circle
                          key={idx}
                          cx={idx * 60 + 30}
                          cy={200 - (point.ratio / Math.max(maxRatio, 100)) * 180}
                          r="4"
                          fill="#28a745"
                        >
                          <title>{`${point.month}: ${point.ratio.toFixed(1)}% (Avg DAU: ${point.avg_dau.toFixed(0)}, MAU: ${point.mau})`}</title>
                        </circle>
                      ))}
                    </svg>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default UserEngagement;
